import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

export function downloadExpenseExcel({ expensesList, dateRange, inRange, allUsersBalances }) {
  const periodLabel = dateRange.label;
  const fromStr = dateRange.from ? format(dateRange.from, 'yyyy-MM-dd') : 'All';
  const toStr = dateRange.to ? format(dateRange.to, 'yyyy-MM-dd') : 'All';

  const periodExpenses = [...expensesList]
    .filter((e) => inRange(e.date))
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  const totalExpenses = periodExpenses.reduce((s, e) => s + (e.amount || 0), 0);
  const sumByStatus = (status) =>
    periodExpenses.filter((e) => e.status === status).reduce((s, e) => s + (e.amount || 0), 0);

  const userToPeriodExpense = {};
  periodExpenses
    .filter((e) => e.status === 'Approved')
    .forEach((e) => {
      const key = e.entered_by || 'Unknown';
      userToPeriodExpense[key] = (userToPeriodExpense[key] || 0) + (e.amount || 0);
    });

  const employeeRows = (allUsersBalances || []).map((b) => ({
    'Employee': b.user_name,
    'Email': b.user_email,
    'Total Cash Received (All-Time)': Number((b.total_advances || 0).toFixed(2)),
    'Total Approved Expenses (All-Time)': Number((b.total_approved_expenses || 0).toFixed(2)),
    'Current Balance': Number((b.current_balance || 0).toFixed(2)),
    'Approved Expenses (Selected Period)': Number(
      (userToPeriodExpense[b.user_name] || 0).toFixed(2)
    ),
  }));

  if (employeeRows.length === 0) {
    Object.entries(userToPeriodExpense).forEach(([name, amt]) => {
      employeeRows.push({
        Employee: name,
        Email: '—',
        'Total Cash Received (All-Time)': 0,
        'Total Approved Expenses (All-Time)': 0,
        'Current Balance': 0,
        'Approved Expenses (Selected Period)': Number(amt.toFixed(2)),
      });
    });
  }

  const summaryAOA = [
    ['Expense Report'],
    [`Period: ${periodLabel}`],
    [`From: ${fromStr}    To: ${toStr}`],
    [`Generated: ${format(new Date(), 'yyyy-MM-dd HH:mm')}`],
    [],
    ['Summary (Selected Period)'],
    ['Total Expenses', Number(totalExpenses.toFixed(2))],
    ['Approved', Number(sumByStatus('Approved').toFixed(2))],
    ['Pending', Number(sumByStatus('Pending').toFixed(2))],
    ['Rejected', Number(sumByStatus('Rejected').toFixed(2))],
    ['Expense Count', periodExpenses.length],
    [],
    ['Employee Funds & Expenses Summary'],
    [
      'Employee',
      'Email',
      'Total Cash Received (All-Time)',
      'Total Approved Expenses (All-Time)',
      'Current Balance',
      'Approved Expenses (Selected Period)',
    ],
    ...employeeRows.map((r) => [
      r['Employee'],
      r['Email'],
      r['Total Cash Received (All-Time)'],
      r['Total Approved Expenses (All-Time)'],
      r['Current Balance'],
      r['Approved Expenses (Selected Period)'],
    ]),
    [],
    ['Expense Details (Selected Period)'],
    [
      'Expense ID', 'Date', 'Vendor/Payee', 'Category', 'Subcategory', 'Description',
      'Amount (INR)', 'Payment Mode', 'Reference No', 'Entered By', 'Status',
      'Approved By', 'Approved At', 'Notes',
    ],
    ...periodExpenses.map((e) => [
      e.expense_id,
      e.date ? format(new Date(e.date), 'yyyy-MM-dd') : '',
      e.vendor_payee,
      e.category,
      e.subcategory || '',
      e.description,
      Number((e.amount || 0).toFixed(2)),
      e.payment_mode,
      e.ref_no || '',
      e.entered_by,
      e.status,
      e.approved_by || '',
      e.approved_at ? format(new Date(e.approved_at), 'yyyy-MM-dd HH:mm') : '',
      e.notes || '',
    ]),
  ];

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(summaryAOA);
  ws['!cols'] = [
    { wch: 18 }, { wch: 28 }, { wch: 22 }, { wch: 18 }, { wch: 18 },
    { wch: 40 }, { wch: 14 }, { wch: 14 }, { wch: 16 }, { wch: 18 },
    { wch: 12 }, { wch: 18 }, { wch: 20 }, { wch: 30 },
  ];
  XLSX.utils.book_append_sheet(wb, ws, 'Expense Report');

  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([wbout], { type: 'application/octet-stream' });
  const fname = `expense-report_${periodLabel.replace(/\s+/g, '-')}_${fromStr}_to_${toStr}.xlsx`;
  saveAs(blob, fname);
}
